import React, { useEffect, useRef, createElement, Component } from 'react';
// Función para verificar si fbq está disponible
const isFbqAvailable = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function' && window.fbq.loaded === true;
};
// Función para intentar recargar el Pixel si no está disponible
const tryReloadPixel = () => {
  if (!isFbqAvailable()) {
    console.warn('Meta Pixel no encontrado o no inicializado correctamente, intentando recargar...');
    // Crear una función fbq temporal si no existe
    if (typeof window !== 'undefined') {
      if (typeof window.fbq !== 'function') {
        window.fbq = function () {
          // Crear una cola temporal si fbq no está disponible
          if (!window._fbq_calls) window._fbq_calls = [];
          window._fbq_calls.push(arguments);
          console.log('Evento en cola hasta que fbq esté disponible:', arguments);
        };
      }
      // Cargar el script de Facebook
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      script.async = true;
      script.onload = function () {
        console.log('Script de Meta Pixel recargado desde MetaPixelTracker');
        // Inicializar si no se ha hecho
        if (typeof window.fbq === 'function' && !window.fbq.loaded) {
          window.fbq('init', '773967181920012');
          window.fbq('track', 'PageView');
          window.fbq.loaded = true;
        }
        // Procesar eventos en cola
        if (window._fbq_calls && window._fbq_calls.length) {
          console.log('Procesando cola de eventos:', window._fbq_calls.length);
          window._fbq_calls.forEach(args => {
            window.fbq.apply(null, args);
          });
          delete window._fbq_calls;
        }
      };
      // Detectar errores de carga
      script.onerror = function () {
        console.error('Error al cargar el script de Meta Pixel desde MetaPixelTracker');
        // Implementar fallback con imagen
        const img = new Image(1, 1);
        img.src = 'https://www.facebook.com/tr?id=773967181920012&ev=PageView&noscript=1&cd[scriptError]=true';
        document.body.appendChild(img);
      };
      document.head.appendChild(script);
      return false;
    }
  }
  return true;
};
// Función segura para llamar a fbq con reintentos
const safeFbq = (action: string, event: string, params?: any) => {
  let attempts = 0;
  const maxAttempts = 3;
  const attemptSend = () => {
    attempts++;
    if (isFbqAvailable()) {
      try {
        window.fbq(action, event, params);
        console.log(`Meta Pixel: ${event} rastreado exitosamente (intento ${attempts})`, params);
        return true;
      } catch (error) {
        console.error(`Error al rastrear ${event} (intento ${attempts}):`, error);
        // Si hay error pero no hemos alcanzado el máximo de intentos, reintentamos
        if (attempts < maxAttempts) {
          console.log(`Reintentando evento ${event} en 500ms...`);
          setTimeout(attemptSend, 500);
          return false;
        }
      }
    } else {
      console.warn(`No se pudo rastrear ${event}: Meta Pixel no disponible (intento ${attempts})`);
      // Intentar recargar el pixel
      tryReloadPixel();
      // Si no hemos alcanzado el máximo de intentos, reintentamos
      if (attempts < maxAttempts) {
        console.log(`Reintentando evento ${event} en 1000ms...`);
        setTimeout(attemptSend, 1000);
        return false;
      } else {
        // Último recurso: usar una imagen directa para el evento
        try {
          const encodedParams = params ? encodeURIComponent(JSON.stringify(params)) : '';
          const img = new Image(1, 1);
          img.src = `https://www.facebook.com/tr?id=773967181920012&ev=${encodeURIComponent(event)}&noscript=1&cd[params]=${encodedParams}`;
          document.body.appendChild(img);
          console.log(`Evento ${event} enviado mediante imagen de fallback`);
        } catch (e) {
          console.error('Error al enviar evento mediante fallback:', e);
        }
      }
    }
    return false;
  };
  return attemptSend();
};
export function MetaPixelTracker() {
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    // Verificar si fbq está disponible
    let pixelAvailable = tryReloadPixel();
    if (!pixelAvailable) {
      console.warn('Meta Pixel no disponible al cargar el componente');
      // Intentar verificar cada segundo durante 15 segundos
      let attempts = 0;
      const maxAttempts = 15;
      const checkInterval = setInterval(() => {
        attempts++;
        if (isFbqAvailable() || attempts >= maxAttempts) {
          clearInterval(checkInterval);
          if (isFbqAvailable()) {
            console.log('Meta Pixel encontrado después de intentos:', attempts);
            initializeTracking();
          } else {
            console.error('No se pudo cargar Meta Pixel después de múltiples intentos');
            // Implementar solución de fallback final
            const fallbackImg = new Image(1, 1);
            fallbackImg.src = 'https://www.facebook.com/tr?id=773967181920012&ev=PageView&noscript=1&cd[failedAfterRetries]=true';
            document.body.appendChild(fallbackImg);
          }
        } else {
          console.log(`Intento ${attempts}/${maxAttempts}: Meta Pixel aún no disponible`);
          // Cada 3 intentos, intentar recargar el script
          if (attempts % 3 === 0) {
            tryReloadPixel();
          }
        }
      }, 1000);
    } else {
      initializeTracking();
    }
    function initializeTracking() {
      // Asegurar que estamos en el navegador y que fbq existe
      if (typeof window === 'undefined' || !isFbqAvailable()) {
        console.warn('No se puede inicializar el rastreo: Meta Pixel no disponible');
        return;
      }
      console.log('Inicializando rastreo de Meta Pixel');
      // Track page view con verificación
      safeFbq('track', 'PageView');
      // Configurar rastreo de scroll
      let scrollDepthTriggered = {
        25: false,
        50: false,
        75: false,
        90: false
      };
      const trackScrollDepth = () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;
        const docHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight, document.documentElement.clientHeight, document.documentElement.scrollHeight, document.documentElement.offsetHeight);
        const scrollPercent = scrollTop / (docHeight - windowHeight) * 100;
        if (scrollPercent >= 25 && !scrollDepthTriggered[25]) {
          safeFbq('trackCustom', 'ScrollDepth25');
          scrollDepthTriggered[25] = true;
        }
        if (scrollPercent >= 50 && !scrollDepthTriggered[50]) {
          safeFbq('trackCustom', 'ScrollDepth50');
          scrollDepthTriggered[50] = true;
        }
        if (scrollPercent >= 75 && !scrollDepthTriggered[75]) {
          safeFbq('trackCustom', 'ScrollDepth75');
          scrollDepthTriggered[75] = true;
        }
        if (scrollPercent >= 90 && !scrollDepthTriggered[90]) {
          safeFbq('trackCustom', 'ScrollDepth90');
          scrollDepthTriggered[90] = true;
        }
      };
      // Rastrear tiempo en página
      const startTime = new Date().getTime();
      const timeIntervals = [30, 60, 120, 300]; // 30s, 1m, 2m, 5m
      const timeouts = [];
      timeIntervals.forEach(seconds => {
        const timeout = setTimeout(() => {
          safeFbq('trackCustom', 'TimeOnPage', {
            seconds: seconds
          });
        }, seconds * 1000);
        timeouts.push(timeout);
      });
      // Rastrear clics en todos los enlaces y botones
      const trackAllClicks = e => {
        const target = e.target.closest('a, button');
        if (target) {
          const tagName = target.tagName.toLowerCase();
          const text = target.innerText || '';
          const href = target.href || '';
          const classes = target.className || '';
          safeFbq('trackCustom', 'ElementClick', {
            element_type: tagName,
            element_text: text,
            element_url: href,
            element_classes: classes
          });
        }
      };
      // Rastrear formularios
      const trackFormSubmits = e => {
        if (e.target.tagName.toLowerCase() === 'form') {
          const formId = e.target.id || 'unknown_form';
          const formAction = e.target.action || 'unknown_action';
          safeFbq('trackCustom', 'FormSubmit', {
            form_id: formId,
            form_action: formAction,
            timestamp: new Date().toISOString()
          });
        }
      };
      // Agregar event listeners
      window.addEventListener('scroll', trackScrollDepth);
      document.addEventListener('click', trackAllClicks);
      document.addEventListener('submit', trackFormSubmits);
      // Verificación periódica del estado del pixel
      const pixelHealthCheck = setInterval(() => {
        if (!isFbqAvailable()) {
          console.warn('Meta Pixel ya no está disponible. Intentando restaurar...');
          tryReloadPixel();
        }
      }, 30000); // Verificar cada 30 segundos
      // Cleanup
      return () => {
        window.removeEventListener('scroll', trackScrollDepth);
        document.removeEventListener('click', trackAllClicks);
        document.removeEventListener('submit', trackFormSubmits);
        timeouts.forEach(timeout => clearTimeout(timeout));
        clearInterval(pixelHealthCheck);
      };
    }
  }, []);
  // Componente invisible para verificación visual
  return <>
      <div id="meta-pixel-verification" style={{
      display: 'none'
    }} data-id="element-355">
        Meta Pixel Verification
      </div>
      <noscript data-id="element-356">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=773967181920012&ev=PageView&noscript=1" alt="Meta Pixel" data-id="element-357" />
      </noscript>
    </>;
}
// Función para rastrear clics en botones de compra
export function trackPurchaseClick(buttonName: string) {
  if (!isFbqAvailable() && !tryReloadPixel()) {
    console.error('No se pudo rastrear clic de compra: Meta Pixel no disponible');
    // Usar fallback con imagen
    const img = new Image(1, 1);
    img.src = `https://www.facebook.com/tr?id=773967181920012&ev=InitiateCheckout&noscript=1&cd[button_name]=${encodeURIComponent(buttonName)}`;
    document.body.appendChild(img);
    return;
  }
  safeFbq('track', 'InitiateCheckout', {
    content_name: 'ImpulseX 30ML',
    content_category: 'Activación Masculina',
    content_ids: ['IMPULSEX30'],
    value: 74990,
    currency: 'CLP',
    button_name: buttonName
  });
  // También rastrear como evento personalizado para mejor atribución
  safeFbq('trackCustom', 'PurchaseButtonClick', {
    button_name: buttonName,
    timestamp: new Date().toISOString()
  });
}
// Función para rastrear vistas de secciones
export function trackSectionView(sectionName: string) {
  if (!isFbqAvailable() && !tryReloadPixel()) {
    console.error(`No se pudo rastrear vista de sección ${sectionName}: Meta Pixel no disponible`);
    // Usar fallback con imagen
    const img = new Image(1, 1);
    img.src = `https://www.facebook.com/tr?id=773967181920012&ev=SectionView&noscript=1&cd[section_name]=${encodeURIComponent(sectionName)}`;
    document.body.appendChild(img);
    return;
  }
  safeFbq('trackCustom', 'SectionView', {
    section_name: sectionName,
    timestamp: new Date().toISOString()
  });
}
// Función para rastrear interacciones con contenido
export function trackContentEngagement(contentType: string, contentName: string, action: string) {
  if (!isFbqAvailable() && !tryReloadPixel()) {
    console.error(`No se pudo rastrear interacción con contenido ${contentName}: Meta Pixel no disponible`);
    // Usar fallback con imagen
    const img = new Image(1, 1);
    img.src = `https://www.facebook.com/tr?id=773967181920012&ev=ContentEngagement&noscript=1&cd[content_type]=${encodeURIComponent(contentType)}&cd[content_name]=${encodeURIComponent(contentName)}&cd[action]=${encodeURIComponent(action)}`;
    document.body.appendChild(img);
    return;
  }
  safeFbq('trackCustom', 'ContentEngagement', {
    content_type: contentType,
    content_name: contentName,
    action: action,
    timestamp: new Date().toISOString()
  });
}
// Función para rastrear interacciones con popups
export function trackPopupInteraction(popupName: string, action: string) {
  if (!isFbqAvailable() && !tryReloadPixel()) {
    console.error(`No se pudo rastrear interacción con popup ${popupName}: Meta Pixel no disponible`);
    // Usar fallback con imagen
    const img = new Image(1, 1);
    img.src = `https://www.facebook.com/tr?id=773967181920012&ev=PopupInteraction&noscript=1&cd[popup_name]=${encodeURIComponent(popupName)}&cd[action]=${encodeURIComponent(action)}`;
    document.body.appendChild(img);
    return;
  }
  safeFbq('trackCustom', 'PopupInteraction', {
    popup_name: popupName,
    action: action,
    timestamp: new Date().toISOString()
  });
}
// Función para rastrear eventos de agregar al carrito
export function trackAddToCart(productId: string, price: number) {
  if (!isFbqAvailable() && !tryReloadPixel()) {
    console.error(`No se pudo rastrear AddToCart para ${productId}: Meta Pixel no disponible`);
    // Usar fallback con imagen
    const img = new Image(1, 1);
    img.src = `https://www.facebook.com/tr?id=773967181920012&ev=AddToCart&noscript=1&cd[content_ids]=${encodeURIComponent(productId)}&cd[value]=${price}`;
    document.body.appendChild(img);
    return;
  }
  safeFbq('track', 'AddToCart', {
    content_ids: [productId],
    content_name: 'ImpulseX 30ML',
    content_type: 'product',
    value: price,
    currency: 'CLP'
  });
}
// Función para rastrear compras completadas
export function trackPurchaseComplete(orderValue: number, currency: string = 'CLP') {
  if (!isFbqAvailable() && !tryReloadPixel()) {
    console.error(`No se pudo rastrear compra completada: Meta Pixel no disponible`);
    // Usar fallback con imagen
    const img = new Image(1, 1);
    img.src = `https://www.facebook.com/tr?id=773967181920012&ev=Purchase&noscript=1&cd[value]=${orderValue}&cd[currency]=${currency}`;
    document.body.appendChild(img);
    return;
  }
  safeFbq('track', 'Purchase', {
    value: orderValue,
    currency: currency,
    content_type: 'product',
    content_ids: ['IMPULSEX30'],
    content_name: 'ImpulseX 30ML',
    content_category: 'Activación Masculina'
  });
}
// Función para verificar manualmente el estado del pixel
export function checkPixelStatus() {
  const status = {
    pixelAvailable: isFbqAvailable(),
    fbqType: typeof window.fbq,
    fbqLoaded: window.fbq?.loaded || false,
    timestamp: new Date().toISOString()
  };
  console.log('Estado del Meta Pixel:', status);
  // Intentar enviar un evento de diagnóstico
  if (status.pixelAvailable) {
    try {
      window.fbq('trackCustom', 'PixelStatusCheck', status);
      console.log('Evento de diagnóstico enviado correctamente');
      return true;
    } catch (e) {
      console.error('Error al enviar evento de diagnóstico:', e);
      return false;
    }
  } else {
    console.warn('No se puede enviar evento de diagnóstico: Meta Pixel no disponible');
    return false;
  }
}
// Declaración global para TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq_calls?: any[];
    fbqLoaded?: boolean;
  }
}